/**
 * Sidebar Navigation Component
 *
 * Pure navigation with version at the bottom.
 * Uses CSS variable-based theming. Active item highlighted with accent border.
 *
 * Collapsible: a footer toggle shrinks the sidebar to an icon rail.
 * In rail mode, hovering an icon shows a styled tooltip with the
 * label. Preference persists in localStorage.
 */

import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import type React from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useFlags } from '../../hooks/useFlags';
import { useNewDashboardCount } from '../../hooks/useNewDashboardCount';
import { IconButton } from '../ui';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  admin?: boolean;
  /** Capability category (kind='capability') a non-admin must hold for the tab
   *  to show. Every user-facing item has one — roles grant tabs through the
   *  admin Role × Category matrix (see src/auth/rbac/capabilities.ts, which
   *  this list mirrors). Admin items are gated by `admin` instead. */
  capability?: string;
  featureFlag?: string;
  children?: Array<{ label: string; path: string }>;
}

const navItems: NavItem[] = [
  { label: 'CompassAI', path: '/compass-ai', icon: '✨', capability: 'compass-ai-access' },
  { label: 'Overview', path: '/', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
    </svg>
  ), capability: 'overview-access' },
  { label: 'Weekly Briefings', path: '/briefings', icon: '🗞️', capability: 'briefings-access' },
  { label: 'My Salesforce', path: '/my-salesforce', icon: '☁️', capability: 'my-salesforce-access' },
  { label: 'Clinical', path: '/clinical', icon: '🏥', capability: 'clinical-access' },
  { label: 'Clinical Success', path: '/clinical-success', icon: '🤝', capability: 'clinical-success-access' },
  // The Finance "Clinical Success Dashboard" workbook (moved here from Clinical Success on 2026-10-27); the flag gates the workbook area.
  { label: 'Finance', path: '/finance', icon: '💰', capability: 'finance-access', featureFlag: 'clinical_success' },
  { label: 'Accounts', path: '/accounts', icon: '🏢', capability: 'accounts-access' },
  { label: 'Meetings', path: '/meetings', icon: '📅', capability: 'meetings-access', featureFlag: 'meetings' },
  { label: 'Scout', path: '/scout', icon: '🔎', capability: 'scout-access', featureFlag: 'scout' },
  { label: 'Dashboards', path: '/dashboards', icon: '📈', capability: 'dashboards-access' },
  { label: 'Files', path: '/dashboards/files', icon: '🗂️', capability: 'files-access' },
  { label: 'Business', path: '/data-explorer', icon: '💼', capability: 'business-access' },
  { label: 'Operations', path: '/operations', icon: '🚀', capability: 'operations-access' },
  { label: 'Product', path: '/product', icon: '📱', capability: 'product-access' },
  // The owner's own Personal Space — everything Compass keeps for them (private to them); the flag is the switch.
  { label: 'Personal Space', path: '/personal', icon: '🗃️', capability: 'personal-space-access', featureFlag: 'personal_space_browser' },
  { label: 'My Settings', path: '/settings', icon: '👤', capability: 'my-settings-access' },
  { label: 'Connectors', path: '/admin/connectors', icon: '🔌', admin: true },
  { label: 'Data Explorer', path: '/admin/data-explorer', icon: '🔭', admin: true },
  { label: 'Sync Monitor', path: '/admin/sync-monitor', icon: '📡', admin: true },
  // Briefing Runs moved into Admin Settings → Briefings → Runs (it renders
  // the same AdminWbrRuns page). The standalone /admin/wbr-runs route stays
  // registered for deep-link compatibility.
  { label: 'Admin Settings', path: '/admin/settings', icon: '⚙️', admin: true },
  // Feature Flags and Cache moved into Admin Settings as tabs 3 & 4.
  // The standalone /admin/feature-flags and /admin/cache routes
  // remain registered for deep-link compatibility.
  { label: 'Reference Data', path: '/admin/reference-data', icon: '📌', admin: true },
  { label: 'Audit Log', path: '/admin/audit-log', icon: '📋', admin: true },
  { label: 'App Log', path: '/admin/app-log', icon: '📜', admin: true },
  // S3 File Browser removed — it rendered the same <S3Explorer/> as Data
  // Explorer → S3 Storage (/admin/data-explorer?tab=s3). The standalone
  // /admin/file-browser route now redirects there.
  { label: 'System Health', path: '/admin/health', icon: '💚', admin: true },
  { label: 'Bake-off Console', path: '/admin/embed-bakeoff', icon: '🧪', admin: true },
];

const COLLAPSED_KEY = 'compass:sidebar:collapsed';
const RBAC_OPEN_KEY = 'compass:sidebar:rbac-open';

function NavLink({
  item,
  isActive,
  collapsed,
  badge = 0,
}: {
  item: NavItem;
  isActive: boolean;
  collapsed: boolean;
  badge?: number;
}) {
  // `position: fixed` so the tooltip escapes the sidebar's overflow
  // clip without needing a portal. Only one NavLink can be hovered
  // at a time, so per-item state is fine.
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);

  return (
    <Link
      to={item.path}
      aria-label={item.label}
      onMouseEnter={(e) => {
        if (!collapsed) return;
        const r = e.currentTarget.getBoundingClientRect();
        setTooltipPos({ top: r.top + r.height / 2, left: r.right + 8 });
      }}
      onMouseLeave={() => setTooltipPos(null)}
      onClick={() => setTooltipPos(null)}
      className={`flex items-center text-sm font-medium py-2.5 ${collapsed ? 'justify-center px-0' : 'px-5'}`}
      style={{
        color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
        background: isActive ? 'var(--accent-glow)' : 'transparent',
        borderLeft: '3px solid',
        borderLeftColor: isActive ? 'var(--accent)' : 'transparent',
        minHeight: '40px',
      }}
    >
      <span
        className={`text-base w-5 text-center shrink-0 ${collapsed ? '' : 'mr-3'}`}
        style={{ position: 'relative' }}
      >
        {item.icon}
        {badge > 0 && (
          <span
            aria-label={`${badge} new`}
            style={{
              position: 'absolute',
              top: -4,
              right: collapsed ? -4 : -6,
              minWidth: 16,
              height: 16,
              borderRadius: 8,
              background: 'var(--red, #ef4444)',
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 3px',
              lineHeight: 1,
              pointerEvents: 'none',
            }}
          >
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </span>
      {!collapsed && <span className="truncate">{item.label}</span>}
      {collapsed && tooltipPos && (
        <span
          role="tooltip"
          style={{
            position: 'fixed',
            top: tooltipPos.top,
            left: tooltipPos.left,
            transform: 'translateY(-50%)',
            background: 'var(--text-primary)',
            color: 'var(--bg-secondary)',
            padding: '6px 10px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
            opacity: 0.96,
          }}
        >
          {item.label}
        </span>
      )}
    </Link>
  );
}

// Module-level cache — fetched once, persists across re-renders
let cachedVersion: { version: string; sha: string; message: string } | null = null;
let versionFetched = false;

/**
 * Collapsible parent nav item with sub-routes (used by RBAC).
 * In rail mode (sidebar collapsed) just renders the parent icon
 * with a tooltip — clicking navigates to the first child.
 */
function NavGroup({
  item,
  pathname,
  collapsed,
  open,
  onToggle,
}: {
  item: NavItem;
  pathname: string;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const children = item.children ?? [];
  const groupActive = pathname.startsWith(item.path);

  if (collapsed) {
    // Rail mode: render the parent as a normal NavLink pointing at the
    // first child. Sub-items are reachable after expanding the sidebar.
    const fallback: NavItem = { ...item, path: children[0]?.path ?? item.path };
    return <NavLink item={fallback} isActive={groupActive} collapsed />;
  }

  return (
    <div>
      {/* eslint-disable-next-line no-restricted-syntax -- nav-group disclosure row sharing the NavLink nav-row chrome (accent-glow bg + 3px active left-border); a navigation composite, not a components/ui Button. Deferred per Track E. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center text-sm font-medium py-2.5 px-5"
        style={{
          color: groupActive ? 'var(--accent)' : 'var(--text-secondary)',
          background: groupActive ? 'var(--accent-glow)' : 'transparent',
          borderLeft: '3px solid',
          borderLeftColor: groupActive ? 'var(--accent)' : 'transparent',
          minHeight: '40px',
          cursor: 'pointer',
        }}
      >
        <span className="text-base w-5 text-center shrink-0 mr-3">{item.icon}</span>
        <span className="truncate flex-1 text-left">{item.label}</span>
        <span style={{ fontSize: 10, opacity: 0.7 }}>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div>
          {children.map((c) => {
            const active = pathname === c.path;
            return (
              <Link
                key={c.path}
                to={c.path}
                className="flex items-center text-sm py-2 pl-12 pr-5"
                style={{
                  color: active ? 'var(--accent)' : 'var(--text-secondary)',
                  background: active ? 'var(--accent-glow)' : 'transparent',
                  borderLeft: '3px solid',
                  borderLeftColor: active ? 'var(--accent)' : 'transparent',
                  fontWeight: active ? 600 : 400,
                  minHeight: '34px',
                }}
              >
                <span className="truncate">{c.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * CompassAI nav entry is just a normal NavLink to the dedicated `/compass-ai`
 * chat page (see `navItems`), so it needs no bespoke component here.
 */
export function Sidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const flags = useFlags();
  const isAdmin = !!user?.isAdmin;
  // Only poll the dashboards API for the badge when the user can open the
  // Dashboards tab — the API is capability-gated exactly like the tab.
  const canOpenDashboards = isAdmin || (user?.capabilities ?? []).includes('dashboards-access');
  const newDashboardCount = useNewDashboardCount(canOpenDashboards);
  const [version, setVersion] = useState(cachedVersion);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [rbacOpen, setRbacOpen] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(RBAC_OPEN_KEY);
      // Default open when on an RBAC page; otherwise honour stored preference.
      if (stored === '1') return true;
      if (stored === '0') return false;
      return location.pathname.startsWith('/admin/rbac');
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // localStorage may be unavailable (private mode) — preference
      // just won't persist; nothing else to do.
    }
  }, [collapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(RBAC_OPEN_KEY, rbacOpen ? '1' : '0');
    } catch {
      // see COLLAPSED_KEY note above
    }
  }, [rbacOpen]);

  // Auto-expand the RBAC group when navigating into an RBAC route.
  useEffect(() => {
    if (location.pathname.startsWith('/admin/rbac')) {
      setRbacOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (versionFetched) return;
    versionFetched = true;
    fetch('/api/version')
      .then((r) => r.json())
      .then((v) => {
        if (v.version && v.version !== 'unknown') {
          cachedVersion = v;
          setVersion(v);
        }
      })
      .catch(() => {});
  }, []);

  const flagEnabled = (item: NavItem) =>
    !item.featureFlag || flags[item.featureFlag] !== false;

  const userItems = navItems.filter((i) => !i.admin && flagEnabled(i));
  const adminItems = navItems.filter((i) => i.admin && flagEnabled(i));
  // Roles control the menu: a user-facing tab shows iff the user holds its
  // capability category (through any active role). /auth/me returns the full
  // catalog for admins, so they always see everything. Admin sidebar items
  // are hardcoded admin-only and not capability-gated.
  const capabilitySet = new Set(user?.capabilities ?? []);
  const visibleUserItems = userItems.filter(
    (i) => !i.capability || isAdmin || capabilitySet.has(i.capability),
  );
  const visibleAdminItems = adminItems;
  const versionLabel = version ? `v${version.version.split('-')[0]}` : null;

  return (
    <aside
      className={`flex-shrink-0 border-r flex flex-col transition-[width] duration-200 ease-out ${
        collapsed ? 'w-14' : 'w-56'
      }`}
      style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)' }}
    >
      <div className="flex-1 overflow-y-auto">
        <nav className="pt-2">
          {visibleUserItems.map((item) => (
            <NavLink
              key={item.path}
              item={item}
              isActive={location.pathname === item.path}
              collapsed={collapsed}
              badge={item.path === '/dashboards' ? newDashboardCount : 0}
            />
          ))}
        </nav>

        {isAdmin && visibleAdminItems.length > 0 && (
          <>
            {collapsed ? (
              <div className="my-2 mx-3" style={{ height: 1, background: 'var(--border)' }} aria-hidden />
            ) : (
              <div className="px-5 mt-5 mb-1">
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                  Administration
                </p>
              </div>
            )}

            <nav>
              {visibleAdminItems.map((item) =>
                item.children ? (
                  <NavGroup
                    key={item.path}
                    item={item}
                    pathname={location.pathname}
                    collapsed={collapsed}
                    open={rbacOpen}
                    onToggle={() => setRbacOpen((o) => !o)}
                  />
                ) : (
                  <NavLink
                    key={item.path}
                    item={item}
                    isActive={location.pathname === item.path}
                    collapsed={collapsed}
                  />
                ),
              )}
            </nav>
          </>
        )}
      </div>

      {/* Footer row — toggle, plus version when expanded. Collapsing
          here (not at the top) keeps the first nav item flush with
          the top of the sidebar. */}
      <div
        className={`border-t flex items-center py-2 ${collapsed ? 'px-2 justify-center' : 'px-3 justify-between'}`}
        style={{ borderColor: 'var(--border)' }}
      >
        {!collapsed && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{versionLabel ?? ''}</span>
        )}
        <IconButton
          tone="muted"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          style={{ fontSize: 12 }}
        >
          {collapsed ? '»' : '«'}
        </IconButton>
      </div>
    </aside>
  );
}
